export const FEEDBACK_SURFACE_PREFIX = "feedback:";

export interface FeedbackSurfaceOption {
    key: string;
    label: string;
    description: string;
}

// AIDEV-NOTE: Stable keys let surveys target exact product feedback forms.
export const FEEDBACK_SURFACE_OPTIONS: FeedbackSurfaceOption[] = [
    {
        key: "session_summary",
        label: "Session summary feedback",
        description: "Thumbs feedback on generated session summaries.",
    },
    {
        key: "session_parts_list",
        label: "Session parts list feedback",
        description: "Feedback on parts detected for a session.",
    },
    {
        key: "session_parts_graph",
        label: "Session parts graph feedback",
        description: "Feedback on the visual parts relationship map.",
    },
    {
        key: "parts_page",
        label: "Parts page feedback",
        description: "Feedback from My Parts and client parts list pages.",
    },
    {
        key: "part_detail",
        label: "Individual part feedback",
        description: "Feedback on one part's details or recap.",
    },
    {
        key: "assessment",
        label: "Assessment feedback",
        description: "Feedback on generated assessment output.",
    },
    {
        key: "assessment_numbers_removed",
        label: "Assessment: numbers removed notice",
        description:
            "Open-ended replies to the 2026-09 notice that assessments no longer show scores.",
    },
    {
        key: "chat",
        label: "Chat feedback",
        description: "Feedback on practitioner consultation chat.",
    },
    {
        key: "soap_note",
        label: "SOAP note feedback",
        description: "Feedback on generated SOAP notes.",
    },
    {
        key: "message_feedback",
        label: "Message thumbs feedback",
        description: "Thumbs feedback on individual chat messages.",
    },
    {
        key: "similarity_auto_link",
        label: "Part suggestion feedback",
        description: "Feedback on automatic part-linking suggestions.",
    },
];

const knownFeedbackSurfaceKeys = new Set(
    FEEDBACK_SURFACE_OPTIONS.map((option) => option.key),
);

export function toFeedbackSurfaceKey(feedbackFormKey: string) {
    return `${FEEDBACK_SURFACE_PREFIX}${feedbackFormKey}`;
}

export function normalizeFeedbackFormKey({
    explicitKey,
    metadata,
    feature,
    partId,
}: {
    explicitKey?: string;
    metadata?: Record<string, unknown>;
    feature?: string;
    partId?: string;
}) {
    if (explicitKey) return explicitKey;

    const metadataKey = metadata?.feedbackFormKey;
    if (typeof metadataKey === "string" && metadataKey) return metadataKey;

    const metadataType = metadata?.type;
    if (
        typeof metadataType === "string" &&
        knownFeedbackSurfaceKeys.has(metadataType)
    ) {
        return metadataType;
    }

    if (feature === "parts list") return "session_parts_list";
    if (feature === "parts graph") return "session_parts_graph";
    if (feature === "parts_page") return "parts_page";
    if (feature === "assessment") return "assessment";
    if (feature === "chat") return "chat";
    if (feature === "soap_note") return "soap_note";
    if (feature === "similarity_auto_link") return "similarity_auto_link";
    if (partId) return "part_detail";

    return "session_summary";
}
